/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import EventEmitter from 'eventemitter3';
import isObject from 'lodash/isObject';
import sortBy from 'lodash/sortBy';

import AnalyticsCycle from '../cycle/analytics-cycle';
import AnalyticsTransport from '../transport/analytics-transport';

/**
 * Správce analytických dat.
 * Přijímá zaznamenaná data a zálohuje je.
 * Řídí analytický cyklus a odesílání uzavřených skupin na server.
 * @implements {EventBus}
 */
class AnalyticsTracker {
  /**
   * Vytvoří instanci.
   * @param {QueueFactory} queueFactory Továrna na fronty asynchronních akcí.
   * @param {Session} session Sezení s nastavením zařízení.
   * @param {Store} store Datový sklad s analytickými daty.
   * @returns {AnalyticsTracker} Nová instance.
   */
  constructor(queueFactory, session, store) {
    if (!isObject(queueFactory)) {
      throw new TypeError(`Invalid queue factory "${queueFactory}".`);
    }
    if (!isObject(session)) {
      throw new TypeError(`Invalid session "${session}".`);
    }
    if (!isObject(store)) {
      throw new TypeError(`Invalid store "${store}".`);
    }
    /**
     * Analytický cyklus.
     * @private
     * @type {AnalyticsCycle}
     */
    this.analyticsCycle = new AnalyticsCycle(store);
    /**
     * Transportér analytických dat.
     * @private
     * @type {AnalyticsTransport}
     */
    this.analyticsTransport = new AnalyticsTransport();
    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter}
     */
    this.emitter = new EventEmitter();
    /**
     * Fronta asynchronních záznamenání analytických dat.
     * @private
     * @type {Queue}
     */
    this.queue = queueFactory.create();
    /**
     * Sezení s nastavením zařízení.
     * @private
     * @type {Session}
     */
    this.session = session;
    /**
     * Datový sklad s analytickými daty.
     * @private
     * @type {Store}
     */
    this.store = store;
    // Navázání naslouchání na instanci.
    this.onCycleEnded = this.onCycleEnded.bind(this);
    this.onError = this.onError.bind(this);
    this.onTransportSuccess = this.onTransportSuccess.bind(this);
    // Naslouchání událostem.
    this.analyticsCycle.on('ended', this.onCycleEnded);
    this.analyticsCycle.on('error', this.onError);
  }

  /**
   * Uvolní všechny zdroje pro garbage collection.
   * @returns {undefined} Nic.
   */
  destroy() {
    // Zrušení naslouchání událostem.
    this.analyticsCycle.off('ended', this.onCycleEnded);
    this.analyticsCycle.destroy();
  }

  /**
   * Zvýší existující počítadlo o jedno přehrání.
   * @param {AnalyticsCounter} analyticsCounter Počítadlo ke zvýšení.
   * @private
   * @returns {Promise.<AnalyticsCounter>} Zvýšené počítadlo.
   */
  incrementCounter(analyticsCounter) {
    return new Promise((resolve) => {
      const played = analyticsCounter.played + 1;
      return resolve(
        this.store.update('analyticsCounter', analyticsCounter.id, { played })
      );
    });
  }

  /**
   * Zaznamená analytická data.
   * @param {ScheduleInfo} scheduleInfo Informace o přehraném médiu k zaznamenání.
   * @returns {Promise.<AnalyticsCounter>} Agregovaná data, do kterých byl proveden záznam.
   */
  hit(scheduleInfo) {
    // TODO #458 Do analytiky počítat pouze přehrání médií, nikoliv šablon.
    return new Promise((resolve) =>
      resolve(
        this.queue.enqueue(() => {
          if (!scheduleInfo.hasMedium()) {
            // Data jsou zaznamenána pouze pokud bylo přehráno médium.
            return;
          }
          const { analyticsCycle } = this;
          if (!analyticsCycle.isStarted()) {
            // Data jsou zaznamenána pouze pokud je spuštěný analytický cyklus.
            // TODO #309 Proč nefunguje analytika?
            // - Vypadá to, že se z nějakého důvodu nenastaví `session.analyticsPeriod`.
            // - Zde se pak logování zastaví.
            return;
          }
          // Zjištění identifikátorů potřebných pro vyhledání analytického počítadla.
          const { analyticsGroup } = analyticsCycle;
          const analyticsGroupId = analyticsGroup.id;
          const medium = scheduleInfo.getMedium();
          const mediumId = medium.id;
          // Nalezení odpovídajícího analytického počítadla.
          const analyticsCounterArray = this.store
            .filter('analyticsCounter')
            .filter(
              (analyticsCounter) =>
                analyticsGroupId === analyticsCounter.analyticsGroupId &&
                mediumId === analyticsCounter.mediumId
            );
          // Rozhodnutí jakou operaci provést dle počtu nalezených počítadel.
          switch (analyticsCounterArray.length) {
            case 0:
              // Pokud počítadlo neexistuje, vytvoří se nové.
              return this.openCounter(analyticsGroup, medium);
            case 1:
              // Pokud bylo nalezeno odpovídající počítadlo, tak se zvýší jeho počet.
              return this.incrementCounter(analyticsCounterArray[0]);
            default:
              // Nalezení jiného počtu počítadel značí nějakou chybu v logice.
              throw new Error('Too many matching counters.');
          }
        })
      )
    );
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na konec analytického cyklu.
   * @param {AnalyticsGroup} analyticsGroup Ukončená analytická skupina.
   * @private
   * @returns {undefined} Nic.
   */
  onCycleEnded(analyticsGroup) {
    try {
      this.send(analyticsGroup).catch(this.onError);
    } catch (error) {
      this.onError(error);
    }
  }

  /**
   * Reakce na chybu.
   * @param {Error} error Chybový objekt.
   * @private
   * @returns {undefined} Nic.
   */
  onError(error) {
    this.emitter.emit('error', error);
  }

  /**
   * Reakce na úspěšné odeslání analytických dat.
   * @param {Array.<AnalyticsCounter>} analyticsCounterArray Odeslaná analytická počítadla.
   * @param {AnalyticsGroup} analyticsGroup Odeslaná analytická skupina.
   * @private
   * @returns {undefined} Nic.
   */
  onTransportSuccess(analyticsCounterArray, analyticsGroup) {
    try {
      this.remove(analyticsCounterArray, analyticsGroup).catch(this.onError);
    } catch (error) {
      this.onError(error);
    }
  }

  /**
   * Otevře nové analytické počítadlo.
   * @param {AnalyticsGroup} analyticsGroup Analytická skupina, do které počítadlo patří.
   * @param {Medium} medium Přehrané médium, ke kterému se počítadlo vztahuje.
   * @private
   * @returns {Promise.<AnalyticsCounter>} Otevřené počítadlo.
   */
  openCounter(analyticsGroup, medium) {
    return new Promise((resolve) =>
      resolve(
        this.store.create('analyticsCounter', {
          analyticsGroupId: analyticsGroup.id,
          mediumId: medium.id,
          name: medium.name,
          played: 1,
          type: medium.type
        })
      )
    );
  }

  /**
   * Odešle analytickou skupinu na server.
   * @param {AnalyticsGroup} analyticsGroup Analytická skupina k odeslání.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po zařazení k odeslání, nebo po odebrání prázdné skupiny.
   */
  send(analyticsGroup) {
    return new Promise((resolve) => {
      const { session } = this;
      const analyticsGroupId = analyticsGroup.id;
      const analyticsCounterArray = this.store
        .filter('analyticsCounter')
        .filter(
          (analyticsCounter) =>
            analyticsGroupId === analyticsCounter.analyticsGroupId
        );
      if (!analyticsCounterArray.length) {
        // Pokud by v analytické skupině nebylo žádné počítadlo, tak se nic neposílá.
        // Analytická skupina je ihned smazána.
        return resolve(this.remove(analyticsCounterArray, analyticsGroup));
      }
      // Odeslání analytické skupiny a jejích počítadel.
      return resolve(
        this.analyticsTransport
          .send(
            analyticsCounterArray,
            analyticsGroup,
            session.deviceId,
            session.serverUrl
          )
          // TODO #121 Příprava na implementaci fronty.
          // - Demonstrační emit události.
          .then(() =>
            this.onTransportSuccess(analyticsCounterArray, analyticsGroup)
          )
      );
    });
  }

  /**
   * Smaže nepotřebná analytická data ze zálohy.
   * @param {Array.<AnalyticsCounter>} analyticsCounterArray Analytická počítadla ke smazání.
   * @param {AnalyticsGroup} analyticsGroup Analytická skupina ke smazání.
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po smazání dat.
   */
  remove(analyticsCounterArray, analyticsGroup) {
    return new Promise((resolve) => {
      const { store } = this;
      // Smazání analytických objektů ve vhodném pořadí pro případ selhání.
      return resolve(
        store
          .destroyMany(
            'analyticsCounter',
            analyticsCounterArray.map((analyticsCounter) => analyticsCounter.id)
          )
          .then(() => store.destroy('analyticsGroup', analyticsGroup.id))
      );
    });
  }

  /**
   * Aktualizuje analytického správce dle nového nastavení v obsahu.
   * Dle nastavení ovládá zapnutí, vypnutí a restart analytického cyklu.
   * @returns {Promise.<AnalyticsGroup>} Vyřeší se po aktualizaci.
   */
  replace() {
    return new Promise((resolve) => {
      let result;
      const { analyticsGroup } = this.analyticsCycle;
      const { analyticsPeriod } = this.session;
      if (analyticsGroup) {
        if (analyticsPeriod) {
          // Pokud je analytický cyklus spuštěný a je nastavena perioda, provede se restart.
          result = this.analyticsCycle.restart(analyticsPeriod);
        } else {
          // Pokud je analytický cyklus spuštěný a není nastavena perioda, vypne se.
          result = this.analyticsCycle.stop();
        }
      } else if (analyticsPeriod) {
        // Pokud je analytický cyklus vypnutý a je nastavena perioda, zapne se.
        result = this.analyticsCycle.start(analyticsPeriod);
      }
      // Pokud je vše vypnuto, nedělá se nic.
      return resolve(result);
    });
  }

  /**
   * Spustí analytického správce.
   * Obnoví zálohovaná data, uzavře skupiny, odešle je na server a pokusí se o spuštění prvního cyklu.
   * @returns {Promise.<AnalyticsGroup>} Vyřeší se po spuštění.
   */
  start() {
    return new Promise((resolve) => {
      if (this.analyticsCycle.analyticsGroup) {
        throw new Error('Another cycle is in progress.');
      }
      const { store } = this;
      return resolve(
        // Obnovení uložených agregovaných dat.
        store
          .findAll('analyticsCounter')
          // Obnovení uložených intervalů.
          .then(() => store.findAll('analyticsGroup'))
          // Operace s obnovenými intervaly.
          .then((analyticsGroupArray) =>
            // Uzavření všech otevřených intervalů.
            // Neuzavřené intervaly při vypnutí zařízení tak budou mít konec nastavený dle dalšího zapnutí.
            analyticsGroupArray
              .filter((analyticsGroup) => !analyticsGroup.closed)
              .reduce(
                (acc, analyticsGroup) =>
                  acc.then(() =>
                    this.analyticsCycle.closeGroup(analyticsGroup)
                  ),
                Promise.resolve()
              )
              // Odeslání všech nyní již uzavřených intervalů, které jsou seřazeny podle data vytvoření.
              // Díky tomu budou odeslány ve stejném pořadí, jako před obnovením.
              // Analytické intervaly se odesílají sériově, což je důležité pro prevenci zahlcení serveru.
              // Případné selhání odeslání jednoho intervalu se přeskakuje a ihned se pokračuje odesílání následujícího intervalu.
              .then(() =>
                sortBy(analyticsGroupArray, 'opened').reduce(
                  (acc, analyticsGroup) =>
                    acc.then(() =>
                      this.send(analyticsGroup).catch(this.onError)
                    ),
                  Promise.resolve()
                )
              )
          )
          // Pokus o spuštění analytiky.
          .then(() => {
            const { analyticsPeriod } = this.session;
            let result;
            if (analyticsPeriod) {
              // Zahájení analytického cyklu.
              result = this.analyticsCycle.start(analyticsPeriod);
            }
            return result;
          })
      );
    });
  }
}

/**
 * Chybová událost.
 * @event AnalyticsTracker#error
 * @param {Error} error Chybové hlášení.
 */

export default AnalyticsTracker;
